/**
 * Adapted from PlayCanvas Engine's gsplat-manager.js renderer-selection ownership at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */

import * as THREE from 'three'
import type { GSplatGraphicsAdapter } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type { GSplatFormat } from '@/plugins/gsplat/lod/GSplatFormat'
import type {
  GSplatPreparedSortParams,
  GSplatSorterSplat,
  OnSortedCallback,
  SortParametersPayload,
  SortRequestResult,
  SortResultMetadata,
} from '@/plugins/gsplat/sorting/GSplatUnifiedSorter'
import type { GSplatPlacement } from '@/plugins/gsplat/unified/GSplatPlacement'
import type { AssetId, Disposable } from '@/shared/types/core'
import type { GSplatVisualActivitySnapshot } from '@/shared/types/gsplat'
import type { GSplatDirectorManager, GSplatManagerConfigurationTransaction } from './GSplatDirector'
import { GSplatHybridRenderer } from './GSplatHybridRenderer'
import type { GSplatHybridRendererScratch } from './GSplatHybridRendererScratch'
import {
  GSPLAT_RENDER_MODE_FORWARD,
  GSPLAT_RENDER_MODE_NONE,
  GSPLAT_RENDER_MODE_SHADOW,
  type GSplatRenderPassMask,
  type GSplatResolvedRenderer,
  hasGSplatRenderPass,
} from './GSplatPipelineTypes'
import { GSplatQuadRenderer } from './GSplatQuadRenderer'
import type {
  GSplatRenderer,
  GSplatRendererDataSource,
  GSplatRenderViewParams,
} from './GSplatRenderer'
import type { GSplatWorkBufferFormatStage } from './world/GSplatWorkBuffer'
import type {
  GSplatWorldBakeStage,
  GSplatWorldSortedStage,
  GSplatWorldStreamingStage,
} from './world/GSplatWorld'
import type { GSplatWorldSplat, GSplatWorldState } from './world/GSplatWorldState'

/** Manager 独占拥有的 World 最小契约；Renderer 只能借用该 World 的 WorkBuffer。 */
export interface GSplatManagerWorld extends Disposable {
  readonly graphicsAdapter: GSplatGraphicsAdapter
  readonly isDestroyed: boolean
  readonly workBuffer: GSplatRendererDataSource
  readonly hasOctreeInstances: boolean
  readonly currentVersion: number
  readonly lastWorldStateVersion: number
  readonly bufferCopyUploaded: number
  readonly bufferCopyTotal: number
  readonly awaitingLodUpdate: boolean
  readonly pendingLoadCount: number
  readonly needsStreamingFrame: boolean
  reconcile?(placements: readonly GSplatPlacement[]): void
  stageFormatSelection?(format: GSplatFormat): GSplatWorkBufferFormatStage | null
  stageActiveFormatSync(): GSplatWorkBufferFormatStage | null
  stageStreamingUpdate(
    camera: THREE.Camera,
    allowLodUpdate: boolean,
    requireCenters: boolean,
    paramsDirty?: boolean
  ): GSplatWorldStreamingStage
  stageBake(version: number, camera: THREE.Camera, updateBounds: boolean): GSplatWorldBakeStage
  stageSorted(
    version: number,
    count: number,
    camera: THREE.Camera,
    updateBounds: boolean,
    orderData?: Uint32Array
  ): GSplatWorldSortedStage | null
  getState(version: number): GSplatWorldState | undefined
  resetFrameStats(): void
  tickCooldowns(logicalFrameToken: number, cooldownTicks: number, frameSource?: object): void
  computeAggregateAabb(): THREE.Box3 | null
  /** 按当前 View 相机的 layer mask 判断资产是否实际进入这个 Manager 的渲染集合。 */
  isAssetVisibleForCamera?(assetId: AssetId, camera: THREE.Camera): boolean
  markInstancesNeedLodUpdate(): void
  getEnvironmentConfigured?(placement: GSplatPlacement): boolean | undefined
  getEnvironmentVisible?(placement: GSplatPlacement): boolean | undefined
  setEnvironmentVisible?(placement: GSplatPlacement, visible: boolean): boolean
  invalidateForRendererSelection(): void
  invalidateForDeviceRestore(): void
}

/** Manager-owned UnifiedSorter 的生命周期与 sorted 事件窄契约。 */
export interface GSplatManagerCpuSorter {
  readonly jobsInFlight: number
  readonly hasPendingSort: boolean
  setOnSortedCallback(callback: OnSortedCallback): void
  setCenters(id: number, centers: Float32Array | null): void
  updateCentersForSplats(splats: readonly GSplatSorterSplat[], includeSortBounds?: boolean): void
  setSortParameters(payload: SortParametersPayload): void
  setPreparedSortParams(
    sortParams: readonly GSplatPreparedSortParams[],
    radialSorting: boolean
  ): SortRequestResult
  applyPendingSorted(): void
  destroy(): void
}

/** GPU-sort forward renderer 的专属 shadow borrower；完整实现由后续 shadow parity 任务提供。 */
export interface GSplatManagerShadowRenderer {
  setDataSource(source: GSplatRendererDataSource): void
  syncLights(): void
  setCastersAabb(aabb: THREE.Box3 | null): void
  cull(params: GSplatRenderViewParams): void
  destroy(): void
}

/** Manager 对宿主 Scene/Camera/Event 的窄适配，不拥有 renderer/sorter/World。 */
export interface GSplatManagerFrameRuntime {
  readonly camera: THREE.Camera
  readonly logicalFrameToken: number
  /** 同一 FrameDriver 下所有 View/Manager 共享的稳定 identity。 */
  readonly logicalFrameSource: object
  readonly radialSorting: boolean
  readonly cooldownTicks: number
  readonly paramsDirty: boolean
  readonly exposure?: number
  readonly useFog: boolean
  readonly cameraFogParams?: unknown
  readonly sceneFog: unknown
  readonly shaderEffectOwner?: AssetId | null
  writeRenderViewParams(target: GSplatRenderViewParams): void
  fireFrameReady(input: {
    readonly camera: THREE.Camera
    readonly layer: unknown
    readonly ready: boolean
    readonly loadingCount: number
  }): void
}

export interface GSplatRendererFactory {
  /** World、renderer 与 sorter GPU leaves 必须全部绑定此 adapter。 */
  readonly graphicsAdapter: GSplatGraphicsAdapter
  readonly frameRuntime: GSplatManagerFrameRuntime
  createWorld(): GSplatManagerWorld
  createQuadRenderer(world: GSplatManagerWorld): GSplatRenderer
  createUnifiedSorter(): GSplatManagerCpuSorter
  createHybridScratch(): GSplatHybridRendererScratch
  createHybridRenderer(
    scratch: GSplatHybridRendererScratch,
    world: GSplatManagerWorld
  ): GSplatRenderer
  createShadowRenderer(
    scratch: GSplatHybridRendererScratch,
    world: GSplatManagerWorld
  ): GSplatManagerShadowRenderer
  subscribeDeviceRestored(callback: () => void): Disposable
}

interface StagedRendererSelection {
  readonly renderer: GSplatRenderer
  readonly cpuSorter: GSplatManagerCpuSorter | null
  readonly shadowRenderer: GSplatManagerShadowRenderer | null
  readonly scratch: GSplatHybridRendererScratch | null
  readonly generation: number
}

/** System transaction 在全部 Manager stage 成功后才提交，并在 Params 发布后统一 finalize。 */
export interface GSplatRendererSelectionStage {
  readonly selection: GSplatResolvedRenderer
  commit(): void
  rollback(): void
  finalize(): void
}

export interface GSplatRendererSelectionParticipant {
  stageRendererSelection(selection: GSplatResolvedRenderer): GSplatRendererSelectionStage
}

export interface GSplatFormatSelectionStage {
  readonly format: GSplatFormat
  commit(): void
  rollback(): void
  finalize(): void
}

export interface GSplatFormatSelectionParticipant {
  stageFormatSelection(format: GSplatFormat): GSplatFormatSelectionStage
}

/**
 * Unified GSplatManager 的 renderer ownership 核心。
 *
 * World 与 active renderer 均为单一 owner；sorter 与 streaming 控制流会在后续 parity 任务继续接入。
 * 本类只持有一个 `GSplatRenderer` 基类引用，禁止重建 legacy 的 nullable Quad/Hybrid 双字段结构。
 */
export class GSplatManager implements GSplatDirectorManager<GSplatPlacement> {
  readonly world: GSplatManagerWorld
  renderer: GSplatRenderer
  activeRenderer: GSplatResolvedRenderer
  renderMode = GSPLAT_RENDER_MODE_NONE
  cpuSorter: GSplatManagerCpuSorter | null
  shadowRenderer: GSplatManagerShadowRenderer | null
  sortNeeded = true

  private readonly rendererFactory: GSplatRendererFactory
  private readonly frameRuntime: GSplatManagerFrameRuntime
  private hybridScratch: GSplatHybridRendererScratch | null = null
  private deviceRestoredSubscription: Disposable | null = null
  private readonly renderViewParams = {} as GSplatRenderViewParams
  // PlayCanvas keeps picker parameters separate from the forward-view bag.  A pick can be
  // prepared mid-frame with a cloned camera and a physical-pixel target; mutating the forward bag
  // here would leak the pick width/height (and surface DPR) into the next forward submission.
  private readonly pickViewParams = {} as GSplatRenderViewParams
  private readonly lastSortCameraPos = new THREE.Vector3(Infinity, Infinity, Infinity)
  private readonly lastSortCameraFwd = new THREE.Vector3(Infinity, Infinity, Infinity)
  private readonly cameraPosition = new THREE.Vector3()
  private readonly cameraDirection = new THREE.Vector3()
  private readonly centersVersions = new Map<number, number>()
  private placements: readonly GSplatPlacement[] = Object.freeze([])
  private lastStreamToken: number | null = null
  private streamAdvanced = false
  private frameUpdateRevision = 0
  private lastUpdateRevision = -1
  private lastUpdateToken: number | null = null
  private lastUpdateCount = 0
  private activeGeneration = 0
  private nextGeneration = 1
  private destroyed = false

  constructor(
    rendererFactory: GSplatRendererFactory,
    initialRenderer: GSplatResolvedRenderer,
    signal?: AbortSignal
  ) {
    this.rendererFactory = rendererFactory
    this.frameRuntime = rendererFactory.frameRuntime
    this.world = rendererFactory.createWorld()
    let staged: StagedRendererSelection | null = null
    try {
      if (this.world.graphicsAdapter !== rendererFactory.graphicsAdapter) {
        throw new Error('GSplatManager World 与 factory 不属于同一 graphics device')
      }
      this.throwIfCreationAborted(signal)
      staged = this.createRendererSelection(initialRenderer)
      this.throwIfCreationAborted(signal)
    } catch (error) {
      if (staged) this.disposeSelectionPreserving(staged)
      this.disposePreserving(() => this.world.dispose())
      throw error
    }
    this.activeRenderer = initialRenderer
    this.renderer = staged.renderer
    this.cpuSorter = staged.cpuSorter
    this.shadowRenderer = staged.shadowRenderer
    this.hybridScratch = staged.scratch
    this.activeGeneration = staged.generation
    let stagedSubscription: Disposable | null = null
    try {
      stagedSubscription = rendererFactory.subscribeDeviceRestored(() => {
        this.handleDeviceRestored()
      })
      this.throwIfCreationAborted(signal)
      this.deviceRestoredSubscription = stagedSubscription
    } catch (error) {
      this.destroyed = true
      this.activeGeneration = this.allocateGeneration()
      this.disposePreserving(() => stagedSubscription?.dispose())
      this.disposeSelectionPreserving(staged)
      this.disposePreserving(() => this.world.dispose())
      throw error
    }
  }

  get sharedHybridScratch(): GSplatHybridRendererScratch | null {
    return this.hybridScratch
  }

  get lifecycleGeneration(): number {
    return this.activeGeneration
  }

  get material(): unknown {
    return this.renderer.material
  }

  getEnvironmentConfigured(placement: GSplatPlacement): boolean | undefined {
    return this.world.getEnvironmentConfigured?.(placement)
  }

  getEnvironmentVisible(placement: GSplatPlacement): boolean | undefined {
    return this.world.getEnvironmentVisible?.(placement)
  }

  setEnvironmentVisible(placement: GSplatPlacement, visible: boolean): boolean {
    return this.world.setEnvironmentVisible?.(placement, visible) ?? false
  }

  get bufferCopyUploaded(): number {
    return this.world.bufferCopyUploaded
  }

  get bufferCopyTotal(): number {
    return this.world.bufferCopyTotal
  }

  get hasPendingSort(): boolean {
    return this.cpuSorter?.hasPendingSort ?? false
  }

  getVisualActivitySnapshot(): GSplatVisualActivitySnapshot {
    const reasons: string[] = []
    if (
      this.world.needsStreamingFrame ||
      this.world.awaitingLodUpdate ||
      this.world.pendingLoadCount > 0
    ) {
      reasons.push('gsplat.streaming')
    }
    if (this.sortNeeded) reasons.push('gsplat.sortNeeded')
    if (this.hasPendingSort) reasons.push('gsplat.pendingSort')
    return { needsNextFrame: reasons.length > 0, reasons }
  }

  reconcile(placements: readonly GSplatPlacement[]): void {
    this.assertActive()
    if (!this.world.reconcile) throw new Error('GSplatManager World 未实现 Placement reconcile')
    const next = Object.freeze([...placements])
    this.world.reconcile(next)
    this.placements = next
    this.invalidateFrameUpdate()
  }

  /**
   * Director topology transaction：stage 完成全部可失败的 World/Renderer 变更，commit 只发布引用。
   * rollback 在 outer transaction 失败时恢复原 Placement 与 render mode。
   */
  stageConfiguration(
    renderMode: GSplatRenderPassMask,
    placements: readonly GSplatPlacement[]
  ): GSplatManagerConfigurationTransaction {
    this.assertActive()
    const previousMode = this.renderMode
    const previousPlacements = this.placements
    const nextPlacements = Object.freeze([...placements])
    let staged = false
    let committed = false
    let settled = false

    try {
      if (!this.world.reconcile) throw new Error('GSplatManager World 未实现 Placement reconcile')
      this.world.reconcile(nextPlacements)
      this.setRenderMode(renderMode)
      staged = true
    } catch (error) {
      try {
        this.world.reconcile?.(previousPlacements)
      } catch {
        // 保留 stage 原始错误。
      }
      try {
        this.setRenderMode(previousMode)
      } catch {
        // 保留 stage 原始错误。
      }
      throw error
    }

    return {
      commit: () => {
        if (settled) throw new Error('GSplat Manager configuration stage 已结束')
        if (committed) return
        this.placements = nextPlacements
        this.invalidateFrameUpdate()
        committed = true
      },
      rollback: () => {
        if (settled) return
        let firstError: unknown
        if (staged) {
          try {
            this.world.reconcile?.(previousPlacements)
          } catch (error) {
            firstError = error
          }
          try {
            this.setRenderMode(previousMode)
          } catch (error) {
            firstError ??= error
          }
        }
        this.placements = previousPlacements
        this.invalidateFrameUpdate()
        settled = true
        if (firstError) throw firstError
      },
    }
  }

  updateStreaming(logicalFrameToken: number): boolean {
    this.assertActive()
    if (logicalFrameToken === this.lastStreamToken) return this.streamAdvanced
    if (!Number.isSafeInteger(logicalFrameToken) || logicalFrameToken < 0) {
      throw new Error(`非法 GSplat logical frame token: ${logicalFrameToken}`)
    }

    const previousSortNeeded = this.sortNeeded
    const formatStage = this.world.stageActiveFormatSync()
    let formatCommitted = false
    let updateStage: GSplatWorldStreamingStage | null = null
    try {
      if (formatStage) {
        formatStage.commit()
        formatCommitted = true
        this.renderer.setDataSource(this.world.workBuffer)
        this.shadowRenderer?.setDataSource(this.world.workBuffer)
      }

      const allowLodUpdate =
        !this.renderer.requiresCpuSort ||
        (this.cpuSorter !== null && this.cpuSorter.jobsInFlight < 3)
      updateStage = this.world.stageStreamingUpdate(
        this.frameRuntime.camera,
        allowLodUpdate,
        this.cpuSorter !== null,
        this.frameRuntime.paramsDirty
      )
      updateStage.commit()
      if (updateStage.overdrawDirty) {
        this.writeRenderViewParams()
        this.renderer.updateOverdrawMode(this.renderViewParams)
      }
      if (updateStage.newVersion && this.cpuSorter) this.feedCpuSorterCenters()
      this.world.tickCooldowns(
        logicalFrameToken,
        this.frameRuntime.cooldownTicks,
        this.frameRuntime.logicalFrameSource
      )

      const advanced =
        updateStage.newVersion || formatStage !== null || this.world.needsStreamingFrame
      this.sortNeeded = previousSortNeeded || updateStage.sortNeeded || formatStage !== null
      this.streamAdvanced = advanced
      this.lastStreamToken = logicalFrameToken
    } catch (error) {
      try {
        updateStage?.rollback()
      } catch {
        // 保留原始 streaming stage 错误。
      }
      if (formatStage) {
        try {
          formatStage.rollback()
        } catch {
          // 保留原始 streaming stage 错误。
        }
        if (formatCommitted) {
          try {
            this.renderer.setDataSource(this.world.workBuffer)
            this.shadowRenderer?.setDataSource(this.world.workBuffer)
          } catch {
            // rollback 已尽力恢复 owner；保留原始错误供同 token 重试。
          }
        }
      }
      this.sortNeeded = previousSortNeeded
      throw error
    }
    updateStage.finalize?.()
    formatStage?.finalize()
    return this.streamAdvanced
  }

  update(): number {
    this.assertActive()
    const logicalFrameToken = this.frameRuntime.logicalFrameToken
    if (
      this.lastUpdateToken === logicalFrameToken &&
      this.lastUpdateRevision === this.frameUpdateRevision
    ) {
      return this.lastUpdateCount
    }
    this.world.resetFrameStats()
    this.updateStreaming(logicalFrameToken)

    this.cpuSorter?.applyPendingSorted()
    if (this.testCameraMovedForSort()) this.sortNeeded = true

    const lastState = this.world.getState(this.world.lastWorldStateVersion)
    if (lastState && this.cpuSorter && !lastState.sortParametersSet) {
      if (this.prepareCpuSortParameters(lastState)) lastState.sortParametersSet = true
    }

    const bakeStage = this.world.stageBake(
      this.world.currentVersion,
      this.frameRuntime.camera,
      this.renderer.requiresBounds
    )
    try {
      bakeStage.prepare?.()
      if (bakeStage.rebuilt) {
        this.renderer.update(bakeStage.count, bakeStage.textureSize)
        this.renderer.setOrderData()
        this.renderer.invalidateCullUpload()
      }
      bakeStage.commit()
    } catch (error) {
      try {
        bakeStage.rollback()
      } catch {
        // 保留 bake/rebuild 的原始错误，下一帧仍可重试。
      }
      try {
        this.renderer.refreshDataSourceBindings()
      } catch {
        // 保留 bake/rebuild 的原始错误；旧 storage 重绑是 best-effort 恢复。
      }
      throw error
    }
    bakeStage.finalize?.()
    if (bakeStage.sortNeeded) this.sortNeeded = true

    if (lastState) {
      let sortAccepted = false
      if (this.renderer.usesGpuSort) {
        this.markGpuSortedIfNeeded(lastState)
        if (hasGSplatRenderPass(this.renderMode, GSPLAT_RENDER_MODE_FORWARD)) {
          this.writeRenderViewParams()
          this.renderer.prepareRenderView(this.world, lastState, this.renderViewParams)
        }
        sortAccepted = true
      } else if (this.sortNeeded) {
        sortAccepted = this.dispatchCpuSort(lastState)
      }

      if (this.sortNeeded && sortAccepted) {
        this.sortNeeded = false
        this.updateSortCameraBaseline()
      }
    }

    this.shadowRenderer?.syncLights()
    const aggregateAabb = this.world.computeAggregateAabb()
    this.renderer.setAggregateAabb(aggregateAabb)
    this.shadowRenderer?.setCastersAabb(aggregateAabb)

    this.frameRuntime.fireFrameReady({
      camera: this.frameRuntime.camera,
      layer: this.renderer.layer,
      ready:
        this.world.currentVersion === this.world.lastWorldStateVersion &&
        !this.world.awaitingLodUpdate,
      loadingCount: this.world.pendingLoadCount,
    })
    if (this.frameRuntime.paramsDirty) this.world.markInstancesNeedLodUpdate()

    this.writeRenderViewParams()
    const fogParams = this.frameRuntime.useFog
      ? (this.frameRuntime.cameraFogParams ?? this.frameRuntime.sceneFog)
      : null
    this.renderer.frameUpdate(this.renderViewParams, this.frameRuntime.exposure, fogParams)
    const count = this.world.getState(this.world.currentVersion)?.totalActiveSplats ?? 0
    this.lastUpdateToken = logicalFrameToken
    this.lastUpdateRevision = this.frameUpdateRevision
    this.lastUpdateCount = count
    return count
  }

  /** ViewFrameContext 更新后允许同一 logical frame 只重跑目标 Manager。 */
  invalidateFrameUpdate(): void {
    this.frameUpdateRevision++
    this.lastStreamToken = null
    this.streamAdvanced = false
  }

  updateShadows(): void {
    this.assertActive()
    if (!this.shadowRenderer) return
    this.writeRenderViewParams()
    this.shadowRenderer.cull(this.renderViewParams)
  }

  prepareForPicking(
    camera: THREE.Camera,
    width: number,
    height: number,
    allowCameraSnapshot = false
  ): unknown | null {
    this.assertActive()
    if (!allowCameraSnapshot && camera !== this.frameRuntime.camera) return null
    const state = this.world.getState(this.world.currentVersion)
    if (!state) return null
    // Keep the forward params untouched while a private pick pass uses a cloned camera.  Width
    // and height are already physical target dimensions at this boundary, therefore pixelRatio is
    // deliberately omitted (PlayCanvas' _fillPickParams has the same contract) and the Hybrid
    // renderer must not multiply them by the surface DPR a second time.
    this.frameRuntime.writeRenderViewParams(this.pickViewParams)
    Object.assign(this.pickViewParams, {
      cameraNode: camera,
      width,
      height,
      pixelRatio: undefined,
      sceneOrigin: this.readSceneOrigin(),
    })
    return this.renderer.preparePickingView(this.world, state, this.pickViewParams)
  }

  setRenderMode(renderMode: GSplatRenderPassMask): void {
    this.assertActive()
    if (renderMode === this.renderMode) return

    const wantShadow = this.wantsDedicatedShadow(this.renderer, renderMode)
    const previousShadow = this.shadowRenderer
    let stagedShadow: GSplatManagerShadowRenderer | null = null
    if (wantShadow && !previousShadow) {
      const scratch = this.hybridScratch
      if (!scratch) throw new Error('GPU GSplat shadow renderer 缺少 Manager-owned scratch')
      stagedShadow = this.rendererFactory.createShadowRenderer(scratch, this.world)
    }

    try {
      this.renderer.setRenderMode(renderMode)
    } catch (error) {
      try {
        this.renderer.setRenderMode(this.renderMode)
      } catch {
        // 保留本次切换的原始错误；Renderer destroy 仍可在后续清理残留 membership。
      }
      this.disposePreserving(() => stagedShadow?.destroy())
      throw error
    }

    this.renderMode = renderMode
    this.shadowRenderer = wantShadow ? (previousShadow ?? stagedShadow) : null
    if (!wantShadow && previousShadow) previousShadow.destroy()
  }

  prepareRendererMode(requested: GSplatResolvedRenderer): void {
    const stage = this.stageRendererSelection(requested)
    try {
      stage.commit()
    } catch (error) {
      try {
        stage.rollback()
      } catch {
        // 保留 mode commit 的原始错误；rollback 已执行 best-effort cleanup。
      }
      throw error
    }
    stage.finalize()
  }

  stageRendererSelection(selection: GSplatResolvedRenderer): GSplatRendererSelectionStage {
    this.assertActive()
    if (selection === this.activeRenderer) {
      return {
        selection,
        commit: () => {},
        rollback: () => {},
        finalize: () => {},
      }
    }

    const previous: StagedRendererSelection = {
      renderer: this.renderer,
      cpuSorter: this.cpuSorter,
      shadowRenderer: this.shadowRenderer,
      scratch: this.hybridScratch,
      generation: this.activeGeneration,
    }
    const previousSelection = this.activeRenderer
    const previousRenderMode = this.renderMode
    const previousCentersVersions = new Map(this.centersVersions)
    const staged = this.createRendererSelection(selection)
    let committed = false
    let settled = false

    return {
      selection,
      commit: () => {
        if (settled) throw new Error('GSplat renderer stage 已结束')
        if (committed) return
        previous.renderer.setRenderMode(GSPLAT_RENDER_MODE_NONE)
        try {
          staged.renderer.setRenderMode(previousRenderMode)
        } catch (error) {
          try {
            previous.renderer.setRenderMode(previousRenderMode)
          } catch {
            // 保留 staged renderer 的原始 commit 错误。
          }
          throw error
        }

        this.renderer = staged.renderer
        this.activeRenderer = selection
        this.cpuSorter = staged.cpuSorter
        this.shadowRenderer = staged.shadowRenderer
        this.hybridScratch = staged.scratch
        this.activeGeneration = staged.generation
        this.centersVersions.clear()
        committed = true
      },
      rollback: () => {
        if (settled) return
        let firstError: unknown
        if (committed) {
          try {
            staged.renderer.setRenderMode(GSPLAT_RENDER_MODE_NONE)
          } catch (error) {
            firstError = error
          }
          try {
            previous.renderer.setRenderMode(previousRenderMode)
          } catch (error) {
            firstError ??= error
          }
          this.renderer = previous.renderer
          this.activeRenderer = previousSelection
          this.cpuSorter = previous.cpuSorter
          this.shadowRenderer = previous.shadowRenderer
          this.hybridScratch = previous.scratch
          this.activeGeneration = previous.generation
          this.centersVersions.clear()
          for (const [id, version] of previousCentersVersions) {
            this.centersVersions.set(id, version)
          }
        }
        try {
          this.disposeSelection(staged, false)
        } catch (error) {
          firstError ??= error
        }
        settled = true
        if (firstError) throw firstError
      },
      finalize: () => {
        if (settled) return
        settled = true
        if (!committed) {
          this.disposeSelection(staged, false)
          return
        }

        let firstError: unknown
        try {
          this.world.invalidateForRendererSelection()
          this.sortNeeded = true
        } catch (error) {
          firstError = error
        }
        try {
          this.disposeSelection(previous, false)
        } catch (error) {
          firstError ??= error
        }
        if (firstError) throw firstError
      },
    }
  }

  stageFormatSelection(format: GSplatFormat): GSplatFormatSelectionStage {
    this.assertActive()
    const worldStage = this.world.stageFormatSelection?.(format)
    if (worldStage === undefined) {
      throw new Error('GSplatManager World 未实现 dataFormat transaction')
    }
    if (worldStage === null) {
      return {
        format,
        commit: () => {},
        rollback: () => {},
        finalize: () => {},
      }
    }

    const renderer = this.renderer
    const previousSortNeeded = this.sortNeeded
    let worldCommitted = false
    let settled = false
    return {
      format,
      commit: () => {
        if (settled) throw new Error('GSplat format stage 已结束')
        if (worldCommitted) return
        worldStage.commit()
        worldCommitted = true
        renderer.onWorkBufferFormatChanged()
        this.sortNeeded = true
      },
      rollback: () => {
        if (settled) return
        let firstError: unknown
        try {
          worldStage.rollback()
        } catch (error) {
          firstError = error
        }
        if (worldCommitted) {
          try {
            renderer.onWorkBufferFormatChanged()
          } catch (error) {
            firstError ??= error
          }
        }
        this.sortNeeded = previousSortNeeded
        settled = true
        if (firstError) throw firstError
      },
      finalize: () => {
        if (settled) return
        settled = true
        worldStage.finalize()
      },
    }
  }

  destroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    this.activeGeneration = this.allocateGeneration()
    const owned = {
      renderer: this.renderer,
      cpuSorter: this.cpuSorter,
      shadowRenderer: this.shadowRenderer,
      scratch: this.hybridScratch,
      generation: this.activeGeneration,
    }
    this.cpuSorter = null
    this.shadowRenderer = null
    this.hybridScratch = null
    this.centersVersions.clear()
    let firstError: unknown
    try {
      this.deviceRestoredSubscription?.dispose()
    } catch (error) {
      firstError = error
    }
    this.deviceRestoredSubscription = null
    try {
      this.disposeSelection(owned, false)
    } catch (error) {
      firstError ??= error
    }
    try {
      this.world.dispose()
    } catch (error) {
      firstError ??= error
    }
    if (firstError) throw firstError
  }

  private createRendererSelection(selection: GSplatResolvedRenderer): StagedRendererSelection {
    const generation = this.allocateGeneration()
    if (selection === 'cpu') {
      const renderer = this.rendererFactory.createQuadRenderer(this.world)
      if (!(renderer instanceof GSplatQuadRenderer)) {
        this.disposePreserving(() => renderer.destroy())
        throw new Error('GSplatRendererFactory.createQuadRenderer 必须返回 GSplatQuadRenderer')
      }
      let sorter: GSplatManagerCpuSorter | null = null
      try {
        sorter = this.rendererFactory.createUnifiedSorter()
        this.bindCpuSorter(sorter, generation)
        return {
          renderer,
          cpuSorter: sorter,
          shadowRenderer: null,
          scratch: null,
          generation,
        }
      } catch (error) {
        this.disposePreserving(() => sorter?.destroy())
        this.disposePreserving(() => renderer.destroy())
        throw error
      }
    }

    const scratch = this.rendererFactory.createHybridScratch()
    let renderer: GSplatRenderer | null = null
    let shadowRenderer: GSplatManagerShadowRenderer | null = null
    try {
      renderer = this.rendererFactory.createHybridRenderer(scratch, this.world)
      if (!(renderer instanceof GSplatHybridRenderer)) {
        throw new Error('GSplatRendererFactory.createHybridRenderer 必须返回 GSplatHybridRenderer')
      }
      if (this.wantsDedicatedShadow(renderer, this.renderMode)) {
        shadowRenderer = this.rendererFactory.createShadowRenderer(scratch, this.world)
      }
      return {
        renderer,
        cpuSorter: null,
        shadowRenderer,
        scratch,
        generation,
      }
    } catch (error) {
      this.disposePreserving(() => shadowRenderer?.destroy())
      this.disposePreserving(() => renderer?.destroy())
      this.disposePreserving(() => scratch.dispose())
      throw error
    }
  }

  private disposeSelection(selection: StagedRendererSelection, suppressError: boolean): void {
    let firstError: unknown
    const dispose = (callback: () => void) => {
      try {
        callback()
      } catch (error) {
        firstError ??= error
      }
    }
    if (selection.cpuSorter) dispose(() => selection.cpuSorter?.destroy())
    if (selection.shadowRenderer) dispose(() => selection.shadowRenderer?.destroy())
    dispose(() => selection.renderer.destroy())
    if (selection.scratch) dispose(() => selection.scratch?.dispose())
    if (!suppressError && firstError) throw firstError
  }

  private assertActive(): void {
    if (this.destroyed) throw new Error('GSplatManager 已销毁')
  }

  private wantsDedicatedShadow(
    renderer: GSplatRenderer,
    renderMode: GSplatRenderPassMask
  ): boolean {
    return renderer.usesGpuSort && hasGSplatRenderPass(renderMode, GSPLAT_RENDER_MODE_SHADOW)
  }

  private allocateGeneration(): number {
    return this.nextGeneration++
  }

  private bindCpuSorter(sorter: GSplatManagerCpuSorter, generation: number): void {
    sorter.setOnSortedCallback((count, version, orderData, sortGeneration, metadata) => {
      if (this.destroyed || generation !== this.activeGeneration || sorter !== this.cpuSorter) {
        metadata?.releaseOrderData?.()
        return
      }
      this.onSorted(count, version, orderData, sortGeneration, metadata)
    })
  }

  /** 应用 live Worker 结果，任何成功/失败/stale 路径都归还 sorter lease。 */
  protected onSorted(
    count: number,
    version: number,
    orderData: Uint32Array,
    _sortGeneration: number,
    metadata?: SortResultMetadata
  ): void {
    try {
      const stage = this.world.stageSorted(
        version,
        count,
        this.frameRuntime.camera,
        this.renderer.requiresBounds,
        orderData
      )
      if (!stage) return
      try {
        stage.prepare()
        if (stage.rebuilt) this.renderer.update(stage.count, stage.textureSize)
        this.renderer.setOrderData()
        stage.commit()
      } catch (error) {
        try {
          stage.rollback()
        } catch {
          // 保留 sort result 应用的原始错误。
        }
        try {
          this.renderer.refreshDataSourceBindings()
        } catch {
          // 保留 sort result 应用的原始错误；旧 storage 重绑是 best-effort 恢复。
        }
        throw error
      }
      stage.finalize?.()
    } finally {
      metadata?.releaseOrderData?.()
    }
  }

  private handleDeviceRestored(): void {
    if (this.destroyed || this.world.hasOctreeInstances) return
    this.world.invalidateForDeviceRestore()
    const generation = this.allocateGeneration()
    this.activeGeneration = generation
    if (this.cpuSorter) this.bindCpuSorter(this.cpuSorter, generation)
    this.sortNeeded = true
  }

  /** 按 Resource 稳定顺序提交 null→centers，成功后才推进 version Map。 */
  protected feedCpuSorterCenters(state?: GSplatWorldState): void {
    const sorter = this.cpuSorter
    if (!sorter) return
    const currentState = state ?? this.world.getState(this.world.lastWorldStateVersion)
    if (!currentState) return

    const nextVersions = new Map<number, number>()
    const uniqueSplats: GSplatWorldSplat[] = []
    for (const splat of currentState.splats) {
      const { resource } = splat
      if (!Number.isSafeInteger(resource.id) || resource.id < 0) {
        throw new Error(`非法 GSplat Resource id: ${resource.id}`)
      }
      if (!Number.isSafeInteger(resource.centersVersion) || resource.centersVersion < 0) {
        throw new Error(
          `非法 GSplat centersVersion: id=${resource.id}, version=${resource.centersVersion}`
        )
      }
      if (!resource.centers) throw new Error(`GSplat Resource[${resource.id}] 缺少 CPU centers`)
      const existingVersion = nextVersions.get(resource.id)
      if (existingVersion !== undefined) {
        if (existingVersion !== resource.centersVersion) {
          throw new Error(
            `GSplat Resource id 版本冲突: id=${resource.id}, first=${existingVersion}, next=${resource.centersVersion}`
          )
        }
        continue
      }
      nextVersions.set(resource.id, resource.centersVersion)
      uniqueSplats.push(splat)
    }

    for (const splat of uniqueSplats) {
      const { resource } = splat
      if (this.centersVersions.get(resource.id) === resource.centersVersion) continue
      sorter.setCenters(resource.id, null)
      sorter.setCenters(resource.id, resource.centers)
    }

    sorter.updateCentersForSplats(currentState.splats, false)
    this.centersVersions.clear()
    for (const [id, version] of nextVersions) this.centersVersions.set(id, version)
  }

  /** 生成 PlayCanvas 对齐的 versioned per-interval WorkBuffer offset payload。 */
  protected prepareCpuSortParameters(state: GSplatWorldState): boolean {
    const sorter = this.cpuSorter
    if (!sorter) return false
    this.feedCpuSorterCenters(state)

    const intervals: number[][] = []
    const pixelOffsets: number[][] = []
    for (const splat of state.splats) {
      const activeIntervals =
        splat.intervals.length > 0 ? [...splat.intervals] : [0, splat.activeSplats]
      const offsets = [...(state.intervalOffsetsBySplat.get(splat) ?? [])]
      if (offsets.length !== activeIntervals.length / 2) {
        throw new Error(
          `GSplat interval offset 数量不匹配：resource=${splat.resource.id}, actual=${offsets.length}, required=${activeIntervals.length / 2}`
        )
      }
      intervals.push(activeIntervals)
      pixelOffsets.push(offsets)
    }

    sorter.setSortParameters({
      textureSize: state.textureSize,
      intervals,
      pixelOffsets,
      version: state.version,
      totalActiveSplats: state.totalActiveSplats,
      ids: state.splats.map((splat) => splat.resource.id),
    })
    return true
  }

  /** 构造单次 operation-local camera/model snapshot，只在 sorter 接受后由调用方推进 baseline。 */
  protected dispatchCpuSort(state: GSplatWorldState): boolean {
    const sorter = this.cpuSorter
    if (!sorter) return false
    const camera = this.frameRuntime.camera
    camera.updateMatrixWorld(true)
    const cameraPosition = new THREE.Vector3().setFromMatrixPosition(camera.matrixWorld)
    const cameraDirection = new THREE.Vector3()
      .setFromMatrixColumn(camera.matrixWorld, 2)
      .normalize()
    const viewProjection = new THREE.Matrix4().multiplyMatrices(
      camera.projectionMatrix,
      camera.matrixWorldInverse
    )
    const sortParams: GSplatPreparedSortParams[] = state.splats.map((splat) => {
      splat.node.updateMatrixWorld(true)
      const modelMatrix = splat.node.matrixWorld.clone()
      const inverseModel = modelMatrix.clone().invert()
      const transformedDirection = cameraDirection.clone().transformDirection(inverseModel)
      const transformedPosition = cameraPosition.clone().applyMatrix4(inverseModel)
      const modelTranslation = new THREE.Vector3().setFromMatrixPosition(modelMatrix)
      const uniformScale = new THREE.Vector3().setFromMatrixColumn(modelMatrix, 0).length()
      const modelViewProjection = new THREE.Matrix4().multiplyMatrices(viewProjection, modelMatrix)

      return {
        id: splat.resource.id,
        transformedDirection,
        transformedPosition,
        offset: modelTranslation.sub(cameraPosition).dot(cameraDirection),
        scale: uniformScale,
        modelMatrix: Array.from(modelMatrix.elements),
        modelViewProjection: Array.from(modelViewProjection.elements),
        aabbMin: splat.aabb.min.toArray() as [number, number, number],
        aabbMax: splat.aabb.max.toArray() as [number, number, number],
      }
    })

    const result = sorter.setPreparedSortParams(sortParams, this.frameRuntime.radialSorting)
    return result.status === 'submitted' || result.status === 'queued'
  }

  /** GPU 首次排序同步推进同一 Manager-owned World version。 */
  protected markGpuSortedIfNeeded(state: GSplatWorldState): void {
    if (state.sortedBefore) return
    const stage = this.world.stageSorted(
      state.version,
      state.totalActiveSplats,
      this.frameRuntime.camera,
      true
    )
    if (!stage) return
    try {
      stage.prepare()
      if (stage.rebuilt) this.renderer.update(stage.count, stage.textureSize)
      stage.commit()
    } catch (error) {
      try {
        stage.rollback()
      } catch {
        // 保留 GPU mark 的原始错误。
      }
      try {
        this.renderer.refreshDataSourceBindings()
      } catch {
        // 保留 GPU mark 的原始错误；旧 storage 重绑是 best-effort 恢复。
      }
      throw error
    }
    stage.finalize?.()
  }

  protected testCameraMovedForSort(): boolean {
    const camera = this.frameRuntime.camera
    camera.updateMatrixWorld(true)
    if (this.frameRuntime.radialSorting) {
      camera.getWorldPosition(this.cameraPosition)
      return this.lastSortCameraPos.distanceTo(this.cameraPosition) > 0.001
    }
    this.cameraDirection.setFromMatrixColumn(camera.matrixWorld, 2).normalize()
    if (!Number.isFinite(this.lastSortCameraFwd.x)) return true
    const dot = THREE.MathUtils.clamp(this.lastSortCameraFwd.dot(this.cameraDirection), -1, 1)
    return Math.acos(dot) > 0.001
  }

  private updateSortCameraBaseline(): void {
    const camera = this.frameRuntime.camera
    camera.updateMatrixWorld(true)
    camera.getWorldPosition(this.lastSortCameraPos)
    this.lastSortCameraFwd.setFromMatrixColumn(camera.matrixWorld, 2).normalize()
  }

  private writeRenderViewParams(): void {
    this.frameRuntime.writeRenderViewParams(this.renderViewParams)
    const owner = this.frameRuntime.shaderEffectOwner
    const ownerVisible =
      owner != null &&
      (this.world.isAssetVisibleForCamera?.(owner, this.frameRuntime.camera) ??
        this.placements.some((placement) => placement.assetId === owner))
    Object.assign(this.renderViewParams, {
      sceneOrigin: this.readSceneOrigin(),
      shaderEffectEnabled: ownerVisible,
    })
  }

  private readSceneOrigin(): THREE.Vector3 | null {
    const state = this.world.getState(this.world.currentVersion)
    for (const splat of state?.splats ?? []) {
      const origin = (splat.node as THREE.Object3D & { __sceneOriginOverride?: THREE.Vector3 })
        .__sceneOriginOverride
      if (origin) return origin.clone()
    }
    return null
  }

  private disposeSelectionPreserving(selection: StagedRendererSelection): void {
    try {
      this.disposeSelection(selection, true)
    } catch {
      // suppressError=true；仅防御不符合契约的实现。
    }
  }

  private disposePreserving(callback: () => void): void {
    try {
      callback()
    } catch {
      // 构造/stage 失败时必须保留原始异常，同时尽力释放后续 owner。
    }
  }

  private throwIfCreationAborted(signal?: AbortSignal): void {
    if (signal?.aborted) throw new DOMException('GSplatManager 创建已取消', 'AbortError')
  }
}
